// Configuration > Referentiels > Vues (BI-693) : referentiel des vues du depot iCTI, en lecture seule
// (alimente par la synchro iCTI). GET /api/icti-views?module=<code>&include_removed=0|1.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Input, Select, Table, Typography, message } from "antd";
import { Link } from "react-router-dom";

import axios from "../../utils/axios";
import { Puces, useUrlParams } from "../utils/PageEntete";
import SettingsHeader from "./SettingsHeader";
import { colonneEtat } from "../Supervision/UsageBadges";
import { comparer, formaterDate, texteSynchro } from "../Supervision/usageVuesFormat";

const liste = (v) => (Array.isArray(v) ? v : []);
const tri = (cle, rendu) => ({
  sorter: comparer(cle),
  sortDirections: ["descend", "ascend"],
  ...(rendu ? { render: rendu } : {}),
});
const pagination = { defaultPageSize: 50, showSizeChanger: true, hideOnSinglePage: true };
const sansAccent = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export default function ParamViews() {
  const [params, majUrl] = useUrlParams();
  const module = params.module || undefined;
  // Statut du référentiel : toutes (défaut, première pastille comme dans Suivi des logs), actives ou supprimées
  const [statut, setStatut] = useState("toutes");
  const [recherche, setRecherche] = useState("");
  const [etat, setEtat] = useState({ chargement: true, erreur: false, data: null });

  useEffect(() => {
    let actif = true;
    setEtat((e) => ({ ...e, chargement: true, erreur: false }));
    // Toujours avec les supprimées : le libellé de l'interrupteur affiche leur nombre, le filtre est local
    const p = { include_removed: 1 };
    if (module) p.module = module;
    axios
      .get("/api/icti-views", { params: p })
      .then((res) => actif && setEtat({ chargement: false, erreur: false, data: (res && res.data) || {} }))
      .catch(() => {
        if (!actif) return;
        setEtat({ chargement: false, erreur: true, data: null });
        message.error("Impossible de charger le référentiel des vues");
      });
    return () => {
      actif = false;
    };
  }, [module]);

  const d = etat.data || {};
  const sync = d.sync || {};
  const modules = liste(d.modules);
  // Les compteurs des pastilles suivent la recherche (et le module, filtré côté API)
  const trouvees = useMemo(() => {
    const q = sansAccent(recherche.trim());
    return liste(d.views).filter((v) => !q || sansAccent(v.code).includes(q) || sansAccent(v.label).includes(q));
  }, [d.views, recherche]);
  const nbTotal = trouvees.length;
  const nbSupprimees = trouvees.filter((v) => v.removed_at).length;
  const vues = useMemo(() => {
    const garder = { actives: (v) => !v.removed_at, supprimees: (v) => !!v.removed_at, toutes: () => true }[statut];
    return trouvees.filter(garder);
  }, [trouvees, statut]);

  const colonnes = [
    { title: "Code", dataIndex: "code", ...tri("code") },
    { title: "Libellé", dataIndex: "label", ...tri("label", (v) => v || "—") },
    colonneEtat((r) => (r.removed_at ? "supprimee" : "active"), (r) => r.declared_name),
    {
      title: "Module",
      dataIndex: "module_label",
      ...tri("module_label", (v, r) =>
        r.module_id !== null && r.module_id !== undefined ? (
          <Link to="/settings?tab=referentiels&sub=modules">{v || r.module}</Link>
        ) : (
          v || r.module || "—"
        )
      ),
    },
    { title: "Ajoutée le", dataIndex: "added_at", ...tri("added_at", (v) => formaterDate(v)) },
  ];

  return (
    <div className="param-views">
      <SettingsHeader title="Vues" />
      {/* Même gabarit que Suivi des logs : pastilles de statut à compteur avec l'état de synchro à droite,
          puis les filtres, puis le tableau */}
      <Puces
        label="Statut des vues"
        value={statut}
        onChange={setStatut}
        items={[
          { key: "toutes", label: "Toutes", count: nbTotal },
          { key: "actives", label: "Actives", count: nbTotal - nbSupprimees },
          { key: "supprimees", label: "Supprimées", count: nbSupprimees },
        ]}
        extra={
          etat.data && (
            <Typography.Text type="secondary" className="cti-bar-end" data-testid="synchro-referentiel">
              {sync.synced_at ? texteSynchro(sync) : "Référentiel jamais synchronisé"}
            </Typography.Text>
          )
        }
      />
      <div className="param-views-filtres" style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "12px 0" }}>
        <Select
          aria-label="Module"
          placeholder="Tous les modules"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220 }}
          value={module}
          onChange={(v) => majUrl({ module: v || null })}
          options={modules.map((m) => ({ value: m.code, label: m.label || m.code }))}
        />
        <Input.Search
          aria-label="Recherche"
          placeholder="Code ou libellé"
          allowClear
          style={{ width: 240 }}
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
      </div>
      {etat.erreur && <Alert type="error" showIcon message="Impossible de charger le référentiel des vues." style={{ marginBottom: 12 }} />}
      <Table
        size="small"
        rowKey="code"
        loading={etat.chargement}
        columns={colonnes}
        dataSource={vues}
        pagination={pagination}
        locale={{ emptyText: "Aucune vue" }}
      />
    </div>
  );
}
